/**
 * Headless hook for the Error Config Viewer
 *
 * This hook provides all the state and logic for displaying error configurations,
 * allowing tenants to provide their own rendering while reusing the data logic.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";

import { getAllErrorEntries } from "./getAllErrorEntries";
import type { ErrorCategory, ErrorEntry, IconStats } from "./types";
import { CATEGORY_LABELS } from "./types";

/**
 * Options for the useErrorConfigViewer hook
 */
export interface UseErrorConfigViewerOptions {
  /**
   * Initial active categories. Defaults to all categories.
   */
  initialCategories?: ErrorCategory[];
}

/**
 * Return type for the useErrorConfigViewer hook
 */
export interface UseErrorConfigViewerResult {
  /** All error entries (unfiltered) */
  allEntries: ErrorEntry[];
  /** Filtered entries based on search and active categories */
  filteredEntries: ErrorEntry[];
  /** Entries grouped by category */
  groupedEntries: Map<ErrorCategory, ErrorEntry[]>;
  /** Currently active categories */
  activeCategories: Set<ErrorCategory>;
  /** Toggle a category on/off */
  toggleCategory: (category: ErrorCategory) => void;
  /** Current search query */
  searchQuery: string;
  /** Set the search query */
  setSearchQuery: (query: string) => void;
  /** Whether to show translation key info */
  showKeyInfo: boolean;
  /** Toggle showing translation key info */
  setShowKeyInfo: (show: boolean) => void;
  /** Currently selected entry index (for full-screen preview) */
  selectedIndex: number | null;
  /** Set the selected entry index */
  setSelectedIndex: (index: number | null) => void;
  /** Currently selected entry (derived from selectedIndex) */
  selectedEntry: ErrorEntry | null;
  /** Navigate to next entry */
  goToNext: () => void;
  /** Navigate to previous entry */
  goToPrev: () => void;
  /** Close the full-screen preview */
  closePreview: () => void;
  /** Find the index of an entry in filteredEntries */
  findEntryIndex: (entry: ErrorEntry) => number;
  /** Icon statistics for the filtered entries */
  iconStats: IconStats;
  /** Category labels */
  categoryLabels: typeof CATEGORY_LABELS;
  /** Check if a translation is missing */
  isMissingTranslation: (key: string, translated: string) => boolean;
  /** Translate a key */
  t: (key: string) => string;
}

const ALL_CATEGORIES: ErrorCategory[] = [
  "flights-quote",
  "flights-fulfill",
  "flights-purchase",
  "flights-seats",
  "lodging-quote",
  "lodging-fulfill",
  "lodging-purchase",
];

/**
 * Headless hook that provides all state and logic for the Error Config Viewer.
 *
 * @example
 * ```tsx
 * const viewer = useErrorConfigViewer();
 *
 * return (
 *   <div>
 *     <input value={viewer.searchQuery} onChange={(e) => viewer.setSearchQuery(e.target.value)} />
 *     {viewer.filteredEntries.map(entry => (
 *       <MyErrorCard key={entry.errorCode} entry={entry} onClick={() => viewer.setSelectedIndex(i)} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useErrorConfigViewer(
  options: UseErrorConfigViewerOptions = {}
): UseErrorConfigViewerResult {
  const { initialCategories = ALL_CATEGORIES } = options;
  const { t } = useI18nContext();

  const [activeCategories, setActiveCategories] = useState<Set<ErrorCategory>>(
    new Set(initialCategories)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showKeyInfo, setShowKeyInfo] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Build all error entries (memoized, static data)
  const allEntries = useMemo(() => getAllErrorEntries(), []);

  // Filter entries based on search and active categories
  const filteredEntries = useMemo(() => {
    return allEntries.filter((entry) => {
      if (!activeCategories.has(entry.category)) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const title = t(entry.titleKey).toLowerCase();
        const subtitle = t(entry.subtitleKey).toLowerCase();
        return (
          entry.errorCode.toLowerCase().includes(query) ||
          title.includes(query) ||
          subtitle.includes(query)
        );
      }
      return true;
    });
  }, [allEntries, activeCategories, searchQuery, t]);

  // Group entries by category
  const groupedEntries = useMemo(() => {
    const groups = new Map<ErrorCategory, ErrorEntry[]>();
    for (const entry of filteredEntries) {
      if (!groups.has(entry.category)) {
        groups.set(entry.category, []);
      }
      groups.get(entry.category)!.push(entry);
    }
    return groups;
  }, [filteredEntries]);

  // Icon statistics
  const iconStats = useMemo((): IconStats => {
    return {
      missing: filteredEntries.filter((e) => e.className === "missing").length,
      warn: filteredEntries.filter((e) => e.className === "warn").length,
      timeout: filteredEntries.filter((e) => e.className === "timeout").length,
      error: filteredEntries.filter(
        (e) => !e.className || e.className === "error"
      ).length,
    };
  }, [filteredEntries]);

  const toggleCategory = useCallback((category: ErrorCategory) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const isMissingTranslation = useCallback(
    (key: string, translated: string): boolean => {
      return translated === key;
    },
    []
  );

  // Selected entry derived from index
  const selectedEntry =
    selectedIndex !== null ? filteredEntries[selectedIndex] : null;

  // Navigation functions
  const goToNext = useCallback(() => {
    if (selectedIndex === null || filteredEntries.length === 0) return;
    const nextIndex = (selectedIndex + 1) % filteredEntries.length;
    setSelectedIndex(nextIndex);
  }, [selectedIndex, filteredEntries.length]);

  const goToPrev = useCallback(() => {
    if (selectedIndex === null || filteredEntries.length === 0) return;
    const prevIndex =
      (selectedIndex - 1 + filteredEntries.length) % filteredEntries.length;
    setSelectedIndex(prevIndex);
  }, [selectedIndex, filteredEntries.length]);

  const closePreview = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  // Find entry index in filtered list
  const findEntryIndex = useCallback(
    (entry: ErrorEntry): number => {
      return filteredEntries.findIndex(
        (e) => e.category === entry.category && e.errorCode === entry.errorCode
      );
    },
    [filteredEntries]
  );

  // Keyboard navigation when preview is open
  useEffect(() => {
    if (selectedIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goToNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goToPrev();
      } else if (e.key === "Escape") {
        e.preventDefault();
        closePreview();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, goToNext, goToPrev, closePreview]);

  return {
    allEntries,
    filteredEntries,
    groupedEntries,
    activeCategories,
    toggleCategory,
    searchQuery,
    setSearchQuery,
    showKeyInfo,
    setShowKeyInfo,
    selectedIndex,
    setSelectedIndex,
    selectedEntry,
    goToNext,
    goToPrev,
    closePreview,
    findEntryIndex,
    iconStats,
    categoryLabels: CATEGORY_LABELS,
    isMissingTranslation,
    t,
  };
}
